import {AfterViewInit, Component, Directive, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {CollectionViewer, DataSource, SelectionChange} from '@angular/cdk/collections';
import {BehaviorSubject, merge, Observable} from 'rxjs';
import {FlatTreeControl} from '@angular/cdk/tree';
import {catchError, map, take} from 'rxjs/operators';
import {Compbaser} from '../../../components/compbaser/compbases';
import {UserService} from '../../../services/user.service';
import {TranslocoService} from '@ngneat/transloco';
import {ToastrService} from 'ngx-toastr';
import {Lib} from "../../../../libs/Lib";
import {isNullOrUndef} from "../../../common";
import {isNull} from "lodash";

interface DialogData {
    rootToken: string;
    rootEndPoint: string;
    childEndPoint: string;
    selectedItem: TokenValue;
    tokenType: string;
    callBack: (v) => void;
}

interface TokenValue {
    id: string;
    path: string;
}

/** Flat node with expandable and level information */
class TokenItemNode {
    constructor(id = '', name = '', path = '', level = 1, expandable = false, isLoading = false, checked = false, hasChild = false) {
        this.id = id,
            this.name = name;
        this.path = path;
        this.level = level;
        this.expandable = expandable;
        this.isLoading = isLoading;
        this.checked = checked;
        this.hasChild = hasChild;
    }

    id: string;
    name: string;
    path: string;
    level: number;
    expandable: boolean;
    isLoading: boolean;
    checked: boolean;
    hasChild: boolean;
}

/**
 * File database, it can build a tree structured Json object from string.
 * Each node in Json object represents a file or a directory. For a file, it has filename and type.
 * For a directory, it has filename and children (a list of files or directories).
 * The input will be a json object string, and the output is a list of `FileNode` with nested
 * structure.
 */
@Directive()
// tslint:disable-next-line
class DynamicDataSource extends Compbaser implements DataSource<TokenItemNode> {
    dataChange = new BehaviorSubject<TokenItemNode[]>([]);

    get data(): TokenItemNode[] {
        return this.dataChange.value;
    }

    set data(value: TokenItemNode[]) {
        this._treeControl.dataNodes = value;
        this.dataChange.next(value);
    }

    constructor(
        private _treeControl: FlatTreeControl<TokenItemNode>,
        private modal: DialogData,
        private userService: UserService,
        private translocoService: TranslocoService,
        private toastr: ToastrService) {
        super();
    }

    connect(collectionViewer: CollectionViewer): Observable<TokenItemNode[]> {
        this._treeControl.expansionModel.changed.subscribe(change => {
            if ((change as SelectionChange<TokenItemNode>).added ||
                (change as SelectionChange<TokenItemNode>).removed) {
                this.handleTreeControl(change as SelectionChange<TokenItemNode>);
            }
        });

        return merge(collectionViewer.viewChange, this.dataChange).pipe(map(() => this.data));
    }

    disconnect(collectionViewer: CollectionViewer): void {
    }

    /** Handle expand/collapse behaviors */
    handleTreeControl(change: SelectionChange<TokenItemNode>) {
        if (change.added) {
            change.added.forEach(node => this.toggleNode(node, true));
        }
        if (change.removed) {
            change.removed.slice().reverse().forEach(node => this.toggleNode(node, false));
        }
    }

    /**
     * Toggle the node, remove from display list
     */
    toggleNode(node: TokenItemNode, expand: boolean) {
        const endPoint = this.setEndPoint(node);
        if (!endPoint) {
            return;
        }
        const index = this.data.indexOf(node);
        if (index < 0) { // If cannot find the node, no op
            return;
        }

        if (expand) {
            node.isLoading = true;
            this.cod(
                this.userService.getGlobalServiceData(endPoint, {noBody: true}, false)
                    .pipe(take(1),
                        map((r) => {
                            return r;
                        }),
                        catchError(e => {
                            // this.toastr.error(this.translocoService.translate('toast.serverReplyInvalid', {}, this.translocoService.getActiveLang()));
                            if (Lib.DevMode())
                                console.log('problem loading Json tokenized data ' + e);
                            this.setTokenList(node, [], index);
                            return e;
                        }))
                    .subscribe((items: any) => {
                        this.setTokenList(node, items, index);
                    })
            );
        } else {
            let count = 0;
            for (let i = index + 1; i < this.data.length
            && this.data[i].level > node.level; i++, count++) {
            }
            this.data.splice(index + 1, count);
            this.dataChange.next(this.data);
        }
    }

    setEndPoint(node: TokenItemNode) {
        const level = node.level;
        if (level > 1 && this.modal.tokenType == 'drive') {
            return '';
        }

        if (level == 0) {
            return this.modal.rootEndPoint.replace(':TOKEN:', this.modal.rootToken);
        } else {
            return this.modal.childEndPoint
                .replace(':TOKEN:', this.modal.rootToken)
                .replace(':TOKEN1:', node.id);
        }
    }

    setTokenList(node: TokenItemNode, children, index) {
        node.isLoading = false;
        if (!children || children.length == 0 || isNullOrUndef(children?.map)) {
            node.hasChild = false;
            return;
        }
        node.hasChild = true;
        if (this.modal.tokenType == 'drive') {
            const nodes = children.map((item: any) => {
                const name = item.title ? item.title : item.summary;
                const path = node.path ? node.path + '/' + name : name;
                return new TokenItemNode(item.id, name, path, node.level + 1, true);
            });
            this.data.splice(index + 1, 0, ...nodes);

        } else if (this.modal.tokenType == 'calendar' || this.modal.tokenType == 'sheets') {
            const nodes = children.map((item: any) => {
                const name = item.title ? item.title : item.summary;
                const path = node.path ? node.path + '/' + name : name;
                return new TokenItemNode(item.id, name, path, node.level + 1, true);
            });
            this.data.splice(index + 1, 0, ...nodes);
        } else if (this.modal.tokenType == 'facebook' || this.modal.tokenType == 'album') {
            const nodes = children.map((item: any) => {
                const name = item.name;
                const path = node.path ? node.path + '/' + name : name;
                return new TokenItemNode(item.id, name, path, node.level + 1, true);
            });
            this.data.splice(index + 1, 0, ...nodes);

        } else if (this.modal.tokenType == 'dropbox') {
            const nodes = children.map((item: any) =>
                new TokenItemNode(item, item.split("/").pop(), item.replace(/^\//, ''), node.level + 1, true));
            this.data.splice(index + 1, 0, ...nodes);

        } else {
            return;
        }
        this.dataChange.next(this.data);
    }

    // getChildren(node: string): string[] | undefined {
    //     return this.dataMap.get(node);
    // }

    // isExpandable(node: string): boolean {
    //     return this.dataMap.has(node);
    // }
}

@Component({
    selector: 'app-dialog-token-treeview',
    templateUrl: './dialog-token-treeview.component.html',
    styleUrls: ['./dialog-token-treeview.component.scss']
})
export class DialogTokenTreeviewComponent extends Compbaser implements OnInit, AfterViewInit {

    treeControl: FlatTreeControl<TokenItemNode>;
    dataSource: DynamicDataSource;
    rootLevelNode = new TokenItemNode('', 'Root', '', 0, true, false, true, true);
    selectedItem: TokenValue = {id: '', path: ''};
    isSingleClick = true;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: DialogData,
        private userService: UserService,
        private translocoService: TranslocoService,
        private toastr: ToastrService
    ) {
        super();
        this.treeControl = new FlatTreeControl<TokenItemNode>(this.getLevel, this.isExpandable);
        this.dataSource = new DynamicDataSource(this.treeControl, this.data, this.userService, this.translocoService, this.toastr);
    }

    ngOnInit(): void {
        // this.dataSource.data = this.rootLevelNode.map(item => new TokenItemNode('', item, 0, true, false, true, true));
        this.dataSource.data = [this.rootLevelNode];
        if (this.data.selectedItem) {
            this.selectedItem = this.data.selectedItem;
        }
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.treeControl.expand(this.rootLevelNode);
        });
    }

    getLevel = (node: TokenItemNode) => node.level;
    isExpandable = (node: TokenItemNode) => node.expandable;
    hasChild = (_: number, _nodeData: TokenItemNode) => _nodeData.expandable;

    selectItem(node: TokenItemNode, event) {
        this.selectedItem = {
            id: node.id,
            path: node.path
        };
    }

    expandItemByDblclick(node: TokenItemNode, event) {
        // this.cd.detectChanges();
        node.checked = true;
        this.treeControl.toggle(node);
    }

    onClickClose() {
        this.data.callBack(this.selectedItem);
    }

    dropItem(event) {

    }

}
